import React, { useState, useEffect, useMemo } from "react";
import lipsHCLogo from "./../../../../Assets/lipsHCLogo.svg";
import Map, { Marker, Source, Layer } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import ChatSuggestions from "../BookingForm/ChatSuggestions/ChatSuggestions";
import * as turf from "@turf/turf";
import { IoAirplane } from "react-icons/io5";
import useBookingApp from "../../../../hooks/useBookingApp";

const origin = [0, 0]; // fallback coordinates

export default function PCLeftContent({
  preConsultation,
  PCactiveStep,
  mainColor,
  tripCountries,
  logo
}) {
  const { bookingSectionState } = useBookingApp();
  const { companyDetails } = bookingSectionState;
  const pharmacyName = companyDetails?.pharmacies[0].pharmacyName;
  const [viewState, setViewState] = useState({
    longitude: 0,
    latitude: 0,
    zoom: 1.5,
  });
  const [arcs, setArcs] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const location = [pos.coords.longitude, pos.coords.latitude];
        setCurrentLocation(location);
        setViewState({
          longitude: location[0],
          latitude: location[1],
          zoom: 1.5,
        });
      },
      (err) => {
        console.error("Geolocation error:", err);
        setCurrentLocation(origin);
        setViewState({
          longitude: origin[0],
          latitude: origin[1],
          zoom: 2,
        });
      }
    );
  }, []);

  const fullRoute = useMemo(() => {
    if (!currentLocation || tripCountries.length === 0) return [];

    const route = [currentLocation];

    if (tripCountries.length === 1) {
      const dest = [tripCountries[0].latlng[1], tripCountries[0].latlng[0]];
      return [
        ...route,
        dest,
        currentLocation, // Back to origin for single flight
      ];
    }

    return [
      ...route,
      ...tripCountries.map((t) => [t.latlng[1], t.latlng[0]]),
      currentLocation, // Back to origin for multiple flights
    ];
  }, [currentLocation, tripCountries]);

  useEffect(() => {
    if (fullRoute.length < 2 || tripCountries.length === 0) {
      setArcs([]);
      return;
    }

    const newArcs = [];
    for (let i = 0; i < fullRoute.length - 1; i++) {
      const from = fullRoute[i];
      const to = fullRoute[i + 1];
      try {
        const line = turf.greatCircle(from, to, { npoints: 100 });
        const midIndex = Math.floor(line.geometry.coordinates.length / 2);
        const midPoint = line.geometry.coordinates[midIndex];
        newArcs.push({
          id: `${from.join("-")}-${to.join("-")}-${i}`,
          coordinates: line.geometry.coordinates,
          midPoint: midPoint,
          destination: to,
          origin: from,
        });
      } catch (error) {
        console.error("Arc error:", error);
      }
    }
    setArcs(newArcs);
  }, [fullRoute, tripCountries]);

  const OriginMarker = () =>
    currentLocation && (
      <Marker
        longitude={currentLocation[0]}
        latitude={currentLocation[1]}
        anchor="bottom"
      >
        <div
          style={{
            background: mainColor || "#007AFF",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            border: "2px solid white",
            boxShadow: "0 0 5px rgba(0,0,0,0.3)",
          }}
        />
      </Marker>
    );

  const DestinationMarker = ({ longitude, latitude }) => (
    <Marker longitude={longitude} latitude={latitude} anchor="bottom">
      <div
        style={{
          background: "#FF3B30",
          borderRadius: "50%",
          width: "15px",
          height: "15px",
          border: "2px solid white",
          boxShadow: "0 0 5px rgba(0,0,0,0.3)",
        }}
      />
    </Marker>
  );

  const AirplaneMarker = ({ longitude, latitude, bearing }) => {
    const newBearing = bearing - 90;
    const darkenHexColor = (hex, amount = 20) => {
      if (hex.charAt(0) === "#") hex = hex.slice(1);
      let r = parseInt(hex.substring(0, 2), 16);
      let g = parseInt(hex.substring(2, 4), 16);
      let b = parseInt(hex.substring(4, 6), 16);
      r = Math.max(0, r - amount);
      g = Math.max(0, g - amount);
      b = Math.max(0, b - amount);
      return `#${((1 << 24) | (r << 16) | (g << 8) | b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;
    };
    const darkenedColor = darkenHexColor(mainColor || "#007AFF", 60);

    return (
      <Marker longitude={longitude} latitude={latitude} anchor="center">
        <div
          style={{
            transform: `rotate(${newBearing}deg)`,
          }}
        >
          <IoAirplane color={darkenedColor} size={20} />
        </div>
      </Marker>
    );
  };

  const renderedArcs = useMemo(() => {
    if (tripCountries.length === 0) return null;

    return arcs.map((arc, index) => {
      const midPoint = arc.midPoint;
      const bearing = turf.bearing(
        turf.point(arc.origin),
        turf.point(arc.destination)
      );

      const showAirplane =
        (tripCountries.length === 1 && index === 0) ||
        (tripCountries.length > 1 && index % 2 === 0);

      return (
        <React.Fragment key={arc.id}>
          <Source
            id={`arc-source-${arc.id}`}
            type="geojson"
            data={{
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: arc.coordinates,
              },
              properties: {},
            }}
          >
            <Layer
              id={`arc-layer-${arc.id}`}
              type="line"
              paint={{
                "line-color": mainColor || "#007AFF",
                "line-dasharray": tripCountries.length === 1 ? [1, 1] : [2, 2],
                "line-width": 2,
                "line-opacity": tripCountries.length === 1 ? 0.7 : 1,
              }}
            />
          </Source>
          {showAirplane && (
            <AirplaneMarker
              longitude={midPoint[0]}
              latitude={midPoint[1]}
              bearing={bearing}
            />
          )}
        </React.Fragment>
      );
    });
  }, [arcs, mainColor, tripCountries]);

  return (
    <div className="inner-content">
      <img className="logo" src={logo} alt={pharmacyName} />
      <div className="content">
        <div className="map-container">
          <Map
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            style={{ width: "100%", height: "100%" }}
            mapStyle="mapbox://styles/mapbox/light-v11"
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            attributionControl={false}
            projection="globe"
            scrollZoom={false}
          >
            <OriginMarker />
            {tripCountries.map((dest, index) => (
              <DestinationMarker
                key={`dest-${index}`}
                longitude={dest.latlng[1]}
                latitude={dest.latlng[0]}
              />
            ))}
            {renderedArcs}
          </Map>
        </div>
        <ChatSuggestions
          preConsultation={preConsultation}
          PCactiveStep={PCactiveStep}
        />
      </div>
    </div>
  );
}
